import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API } from "@/config";

/**
 * Coupled Postal Code + City autocomplete for French addresses.
 * - Typing a postal code (>=2 digits) suggests matching cities.
 * - Typing city name suggests both name + a postal code list.
 * - Selecting a suggestion fills both fields.
 *
 * Props:
 *   postalCode, city : current values (controlled)
 *   onChange({ postal_code, city }) : called with merged updates
 *   required           : mark labels with a red asterisk
 *   testidPrefix       : prefix for data-testids (default 'city-postal')
 *   error              : boolean → show red border (validation)
 *   labels             : { postal: string, city: string } optional overrides
 */
export default function CityPostalAutocomplete({
  postalCode = "",
  city = "",
  onChange,
  required = false,
  testidPrefix = "city-postal",
  error = false,
  labels = { postal: "Code postal", city: "Ville" },
}) {
  const [postalInput, setPostalInput] = useState(postalCode);
  const [cityInput, setCityInput] = useState(city);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // "postal" | "city" | null
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // External resets
  useEffect(() => { setPostalInput(postalCode); }, [postalCode]);
  useEffect(() => { setCityInput(city); }, [city]);

  // Click outside → close
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setActive(null);
        setHighlight(-1);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = useCallback(async (params) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/geo/cities`, { params });
      setSuggestions(r.data || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onPostalChange = (v) => {
    const cleaned = v.replace(/\D/g, "").slice(0, 5);
    setPostalInput(cleaned);
    onChange?.({ postal_code: cleaned, city: cityInput });
    setActive("postal");
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (cleaned.length >= 2) {
      debounceRef.current = setTimeout(() => search({ postal: cleaned }), 200);
    } else {
      setSuggestions([]);
    }
  };

  const onCityChange = (v) => {
    setCityInput(v);
    onChange?.({ postal_code: postalInput, city: v });
    setActive("city");
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length >= 2) {
      debounceRef.current = setTimeout(() => search({ q: v.trim() }), 250);
    } else {
      setSuggestions([]);
    }
  };

  const pickSuggestion = (s, preferPostal = null) => {
    const picked_postal = preferPostal || (s.postal_codes || [])[0] || "";
    setPostalInput(picked_postal);
    setCityInput(s.name);
    onChange?.({ postal_code: picked_postal, city: s.name });
    setSuggestions([]);
    setActive(null);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pickSuggestion(suggestions[highlight]); }
    else if (e.key === "Escape") { setActive(null); setHighlight(-1); }
  };

  const errCls = error ? "border-red-500 ring-1 ring-red-300" : "border-gray-300";

  return (
    <div ref={containerRef} className="relative">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">{labels.postal}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <div className="relative">
            <Input
              value={postalInput}
              onChange={(e) => onPostalChange(e.target.value)}
              onFocus={() => { if (suggestions.length && postalInput.length >= 2) setActive("postal"); }}
              onKeyDown={onKeyDown}
              placeholder="75001"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              className={`rounded-none ${errCls}`}
              data-testid={`${testidPrefix}-postal-input`}
            />
            {loading && active === "postal" && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-[#662483]" />}
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">{labels.city}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
          <div className="relative">
            <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={cityInput}
              onChange={(e) => onCityChange(e.target.value)}
              onFocus={() => { if (suggestions.length && cityInput.length >= 2) setActive("city"); }}
              onKeyDown={onKeyDown}
              placeholder="Paris"
              autoComplete="address-level2"
              className={`rounded-none pl-7 ${errCls}`}
              data-testid={`${testidPrefix}-city-input`}
            />
            {loading && active === "city" && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-[#662483]" />}
          </div>
        </div>
      </div>

      {active && suggestions.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full bg-white border border-gray-200 shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testidPrefix}-suggestions`}
        >
          {suggestions.map((s, i) => {
            // If active is "postal", we filter matching postal to display the selected postal per row
            const focusedPc = active === "postal" ? postalInput : (s.postal_codes[0] || "");
            const rowKey = `${s.insee_code}-${focusedPc}`;
            return (
              <button
                type="button"
                key={rowKey}
                onMouseDown={(e) => e.preventDefault()} // avoid losing focus/blur
                onClick={() => pickSuggestion(s, focusedPc)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 flex items-center gap-2 ${highlight === i ? "bg-[#F0E9F3]" : "hover:bg-[#FAFAFA]"}`}
                data-testid={`${testidPrefix}-suggestion-${s.insee_code}`}
              >
                <MapPin className="w-3.5 h-3.5 text-[#662483] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#3F3F3F] truncate">
                    {s.name} <span className="text-xs text-gray-500 font-mono">({focusedPc})</span>
                  </p>
                  <p className="text-[10px] text-gray-400 truncate">
                    Dép. {s.department} · {s.postal_codes.length} code(s) postal(aux) · {s.population.toLocaleString("fr-FR")} habitants
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
